import config from '../../../../config';
import css from './AwardCard.css';
import React from 'react';
import {
    CREDITS_ACTION_AVAILABILITY_CONFIRMED,
    CREDITS_ACTION_FB_CONTENT_SHARED,
    CREDITS_ACTION_INITIAL_PURCHASE,
    CREDITS_ACTION_LISTING_PUBLISHED,
    CREDITS_ACTION_NEW_RECOMMENDATION_RECEIVED,
    CREDITS_ACTION_PROFILE_COMPLETE,
    CREDITS_ACTION_PROFILE_VERIFIED,
    CREDITS_ACTION_REFERRAL_INVITER,
    CREDITS_ACTION_WHATSAPP_CONTENT_SHARED,
    CREDITS_FLIP_AND_WIN_BONUS_CREDITS,
} from '../../../../credits-config';
import {
    AWARD_TYPE_ACCOUNT,
    AWARD_TYPE_RIDER_PROFILE,
    AWARD_TYPE_HORSE_PROFILE,
    AWARD_TYPE_FLIP_WIN,
} from './awardType';
import { checkIfCreditsEventExists, checkIfCreditsEventRewarded } from '../../../../util/credits';
import { GET_RECOMMENDATIONS_FORM } from '../../../EditUserInfoPage/EditUserInfoPage';

import { getShareViaWhatsAppCurrentEvents } from './utils';
import { createSlug } from '../../../../util/urlHelpers';
import FacebookShareButton from '../../../../components/ShareViaSocialNetworkButtons/FacebookShareButton';
import getWhatsAppSharingData from '../../../../components/ShareViaSocialNetworkButtons/getWhatsAppSharingData';
import { updateCreditsHistory } from '../../../../util/api';
import { fetchCurrentUser } from '../../../../ducks/user.duck';
import { Button } from '../../../../components';
const { userTypeHorseowner, userTypeRider } = config;

const getAwardCards = ({ navigate, currentUser, ownListings, dispatch }) => {
    const currentUserId = currentUser?.id?.uuid;
    const { userType } = currentUser?.attributes?.profile?.publicData || {};
    const isRider = userTypeRider === userType;
    const isHorseowner = userTypeHorseowner === userType;
    const shareViaWhatsAppCurrentEventsCount = getShareViaWhatsAppCurrentEvents(currentUser).length;
    const latestPublishedHorseListing =
        isHorseowner && ownListings
            ? ownListings
                  ?.filter(listing => listing?.attributes?.state === 'published')
                  ?.sort(
                      (a, b) =>
                          new Date(b?.attributes?.createdAt) - new Date(a?.attributes?.createdAt)
                  )[0]
            : null;
    const socialSharingIsAllowed = isHorseowner ? Boolean(latestPublishedHorseListing) : true; // is allowed for rider by default
    const fbSocialSharingRewarded = checkIfCreditsEventRewarded(
        currentUser,
        CREDITS_ACTION_FB_CONTENT_SHARED
    );

    const rewardSharing = async (eventName, extraFields = {}) => {
        await updateCreditsHistory(
            JSON.stringify({
                userId: currentUserId,
                eventName,
                extraFields,
            })
        );
        await dispatch(fetchCurrentUser());
    };

    const getSocialSharingUrl = () => {
        if (isRider) {
            return `${process.env.REACT_APP_CANONICAL_ROOT_URL}/profile/rider/public/${currentUserId}`;
        }

        if (isHorseowner && latestPublishedHorseListing) {
            const slug = createSlug(latestPublishedHorseListing);
            return `${process.env.REACT_APP_CANONICAL_ROOT_URL}/l/${slug}/${latestPublishedHorseListing?.id?.uuid}`;
        }
    };

    const { href } = getWhatsAppSharingData({
        url: getSocialSharingUrl(),
        text: '',
    });

    return [
        {
            id: 'received-invitation',
            credits: '+ 100 Credits',
            heading: 'Einladung erhalten',
            description: 'Registriere dich mit einem Empfehlungslink.',
            actionText: 'Nur mit Link möglich',
            actionAllowed: false,
            show: true,
            type: AWARD_TYPE_ACCOUNT,
        },
        {
            id: 'registration',
            credits: '+ 50 Credits',
            heading: 'Registrierung',
            description: 'Registriere dich ohne Empfehlungslink.',
            actionAllowed: !checkIfCreditsEventExists(currentUser, CREDITS_ACTION_INITIAL_PURCHASE),
            actionHandler: () => navigate('SignupPage'),
            show: true,
            type: AWARD_TYPE_ACCOUNT,
        },
        {
            id: 'invite-friend',
            credits: '+ 50 Credits',
            heading: 'Freund:in einladen',
            description: 'Dein:e Freund:in registriert sich über deinen Empfehlungslink.',
            actionAllowed: !checkIfCreditsEventExists(currentUser, CREDITS_ACTION_REFERRAL_INVITER),
            actionHandler: () => navigate('ReferralPage'),
            show: true,
            type: AWARD_TYPE_ACCOUNT,
        },
        {
            id: 'complete-rider-profile',
            credits: '+ 50 Credits',
            heading: 'Profil vervollständigen',
            description: 'Fülle dein Reiter-Profil zu 100% aus.',
            actionAllowed: !checkIfCreditsEventExists(currentUser, CREDITS_ACTION_PROFILE_COMPLETE),
            actionHandler: () => navigate('ProfileSettingsPage', { userType: userTypeRider }),
            show: !isHorseowner,
            type: AWARD_TYPE_RIDER_PROFILE,
        },
        {
            id: 'confirm-availability',
            credits: '+ 20 Credits',
            heading: 'Verfügbarkeit bestätigen',
            description: 'Bestätige die Verfügbarkeit deines Profils.',
            actionAllowed: !checkIfCreditsEventExists(
                currentUser,
                CREDITS_ACTION_AVAILABILITY_CONFIRMED
            ),
            actionHandler: () =>
                navigate(
                    'ProfileSettingsPage',
                    { userType: userTypeRider },
                    { availabilityStatusChange: true }
                ),
            show: !isHorseowner,
            type: AWARD_TYPE_RIDER_PROFILE,
        },
        {
            id: 'verify-account',
            credits: '+ 20 Credits',
            heading: 'Konto verifizieren',
            description: 'Bestätige E-Mail-Adresse und Telefonnummer.',
            actionAllowed: !checkIfCreditsEventExists(currentUser, CREDITS_ACTION_PROFILE_VERIFIED),
            actionHandler: () => navigate('ContactDetailsPage'),
            show: true,
            type: AWARD_TYPE_ACCOUNT,
        },
        {
            id: 'publish-horse',
            credits: '+ 20 Credits',
            heading: 'Profil veröffentlichen',
            description: 'Erstelle und veröffentliche dein erstes Pferde-Profil.',
            actionAllowed: !checkIfCreditsEventExists(
                currentUser,
                CREDITS_ACTION_LISTING_PUBLISHED
            ),
            actionHandler: () => navigate('NewListingPage'),
            show: !isRider,
            type: AWARD_TYPE_HORSE_PROFILE,
        },
        {
            id: 'recommendation-for-profile',
            credits: '+ 20 Credits',
            heading: 'Empfehlung für Profil erhalten',
            description: 'Erhalte deine erste Empfehlung für dein Reiter-Profil.',
            actionAllowed: !checkIfCreditsEventExists(
                currentUser,
                CREDITS_ACTION_NEW_RECOMMENDATION_RECEIVED
            ),
            actionHandler: () =>
                navigate('EditUserInfo', {
                    section: GET_RECOMMENDATIONS_FORM,
                    userType: userTypeRider,
                }),
            show: !isHorseowner,
            type: AWARD_TYPE_RIDER_PROFILE,
        },
        {
            id: CREDITS_ACTION_FB_CONTENT_SHARED,
            credits: '+ 10 Credits',
            heading: 'Profil auf Facebook teilen',
            description: isHorseowner
                ? 'Teile dein Pferde-Profil mit deinen Freunden bei Facebook.'
                : 'Teile dein Reiter-Profil mit deinen Freunden bei Facebook.',
            actionSubLabel: 'Gutschrift in 48 Stunden',
            actionAllowed: !fbSocialSharingRewarded,
            show: socialSharingIsAllowed,
            type: isHorseowner ? AWARD_TYPE_HORSE_PROFILE : AWARD_TYPE_RIDER_PROFILE,
            renderButton: () =>
                fbSocialSharingRewarded ? (
                    <Button disabled>Reward erhalten</Button>
                ) : (
                    <div
                        onClick={() => rewardSharing(CREDITS_ACTION_FB_CONTENT_SHARED)}
                        style={{ width: '100%' }}
                    >
                        <FacebookShareButton
                            url={getSocialSharingUrl()}
                            text="Reward erhalten"
                            quote={
                                isHorseowner
                                    ? 'Sieh dir diese Pferd bei HorseDeal an!'
                                    : 'Sieh dir diese Reiter-Profil bei HorseDeal an!'
                            }
                            rootClassName={css.shareButton}
                        />
                    </div>
                ),
        },
        {
            id: CREDITS_ACTION_WHATSAPP_CONTENT_SHARED,
            credits: '+ 5 bis 25 Credits',
            heading: 'Profil auf WhatsApp teilen',
            actionText: `Reward ${Math.min(
                shareViaWhatsAppCurrentEventsCount + 1,
                5
            )} von 5 erhalten`,
            actionSubLabel: 'Gutschrift in 48 Stunden',
            description: isHorseowner
                ? 'Teile dein Pferde-Profil mit bis zu 5 Freunden bei WhatsApp.'
                : 'Teile dein Reiter-Profil mit bis zu 5 Freunden bei WhatsApp.',
            actionAllowed: shareViaWhatsAppCurrentEventsCount < 5,
            // !checkIfRewardIsAlreadyDelayed(currentUser, CREDITS_ACTION_WHATSAPP_CONTENT_SHARED),
            actionHandler: () => {
                window.open(href, '_blank');
                rewardSharing(CREDITS_ACTION_WHATSAPP_CONTENT_SHARED, {
                    listingId: latestPublishedHorseListing?.id?.uuid,
                });
            },
            show: socialSharingIsAllowed,
            type: isHorseowner ? AWARD_TYPE_HORSE_PROFILE : AWARD_TYPE_RIDER_PROFILE,
        },
        {
            id: 'flip-and-win',
            credits: '+ 5 bis 100 Credits',
            heading: (
                <>
                    <span>Gewinnen bei</span>{' '}
                    <span style={{ 'white-space': 'nowrap' }}>Flip & Win</span>
                </>
            ),
            description: 'Decke eine Gewinnkarte beim Kartenspiel Flip & Win auf.',
            actionAllowed: !checkIfCreditsEventExists(
                currentUser,
                CREDITS_FLIP_AND_WIN_BONUS_CREDITS
            ),
            actionHandler: () => navigate('FlipAndWin'),
            show: true,
            type: AWARD_TYPE_FLIP_WIN,
        },
    ];
};

export default getAwardCards;
